import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { map, filter, take, catchError } from 'rxjs/operators';
import { PermissionService } from '../services/permission.service';
import { getRoutePermissions, routeRequiresPermissions } from '../config/route-permissions.config';
import { PermissionBit } from '../utils/permission-types';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard implements CanActivate {
  /** Generic "access denied" landing page. */
  private static readonly DENY_REDIRECT = '/staffing/dashboard';

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private auth: AuthService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const routePath = this.getFullRoutePath(state.url);
    const requiredPermissions = getRoutePermissions(routePath);

    if (!this.auth.isLoggedIn()) {
      return this.router.createUrlTree(['/auth']);
    }

    if (!routeRequiresPermissions(routePath)) {
      return true;
    }

    return combineLatest([this.permissionService.ready$, this.permissionService.permissions$]).pipe(
      filter(([ready]) => ready || !this.auth.isLoggedIn()),
      take(1),
      map(([_, userPermissions]) => {
        if (!this.auth.isLoggedIn()) {
          return this.router.createUrlTree(['/auth']);
        }

        return this.checkPermissions(requiredPermissions, userPermissions)
          ? true
          : this.deny(routePath);
      }),
      catchError(() => {
        return of(
          this.auth.isLoggedIn() ? this.deny(routePath) : this.router.createUrlTree(['/auth']),
        );
      }),
    );
  }

  /**
   * Refuses access without ever redirecting to the URL that was just denied:
   * doing so re-enters this guard on the same route and loops forever, which
   * freezes the application. When the deny target is the current route the
   * navigation is simply cancelled.
   */
  private deny(routePath: string): boolean | UrlTree {
    const target = PermissionGuard.DENY_REDIRECT.replace(/^\//, '');
    if (routePath === target) {
      console.warn(`Access denied to route: /${routePath}. Navigation cancelled to avoid a redirect loop.`);
      return false;
    }
    return this.router.createUrlTree([PermissionGuard.DENY_REDIRECT]);
  }

  /**
   * Extract the full route path from URL, removing query parameters and fragments
   */
  private getFullRoutePath(url: string): string {
    // Remove leading slash and clean up the URL
    let cleanUrl = url.startsWith('/') ? url.substring(1) : url;

    // Remove query parameters and fragments
    const queryIndex = cleanUrl.indexOf('?');
    if (queryIndex !== -1) {
      cleanUrl = cleanUrl.substring(0, queryIndex);
    }

    const fragmentIndex = cleanUrl.indexOf('#');
    if (fragmentIndex !== -1) {
      cleanUrl = cleanUrl.substring(0, fragmentIndex);
    }

    return cleanUrl;
  }

  /**
   * Check if user has any of the required permissions
   * Uses 'any' logic - user needs at least one of the required permissions
   */
  private checkPermissions(requiredPermissions: PermissionBit[], userPermissions: bigint): boolean {
    if (requiredPermissions.length === 0) {
      return true;
    }

    return requiredPermissions.some((permission) => (userPermissions & permission) === permission);
  }
}

/**
 * Alternative guard that requires ALL permissions (stricter)
 */
@Injectable({
  providedIn: 'root',
})
export class StrictPermissionGuard implements CanActivate {
  private static readonly DENY_REDIRECT = '/staffing/dashboard';

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private auth: AuthService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const routePath = this.getFullRoutePath(state.url);
    const requiredPermissions = getRoutePermissions(routePath);

    if (!this.auth.isLoggedIn()) {
      return this.router.createUrlTree(['/auth']);
    }

    if (!routeRequiresPermissions(routePath)) {
      return true;
    }

    return combineLatest([this.permissionService.ready$, this.permissionService.permissions$]).pipe(
      filter(([ready]) => ready || !this.auth.isLoggedIn()),
      take(1),
      map(([_, userPermissions]) => {
        if (!this.auth.isLoggedIn()) {
          return this.router.createUrlTree(['/auth']);
        }

        const ok = this.checkAllPermissions(requiredPermissions, userPermissions);
        if (!ok) {
          console.warn(`Access denied to route: ${routePath}. Required ALL permissions:`, requiredPermissions);
          return this.deny(routePath);
        }
        return true;
      }),
    );
  }

  /** Never redirect to the URL that was just denied (it would loop forever). */
  private deny(routePath: string): boolean | UrlTree {
    const target = StrictPermissionGuard.DENY_REDIRECT.replace(/^\//, '');
    if (routePath === target) {
      console.warn(`Access denied to route: /${routePath}. Navigation cancelled to avoid a redirect loop.`);
      return false;
    }
    return this.router.createUrlTree([StrictPermissionGuard.DENY_REDIRECT]);
  }

  private getFullRoutePath(url: string): string {
    let cleanUrl = url.startsWith('/') ? url.substring(1) : url;

    const queryIndex = cleanUrl.indexOf('?');
    if (queryIndex !== -1) {
      cleanUrl = cleanUrl.substring(0, queryIndex);
    }

    const fragmentIndex = cleanUrl.indexOf('#');
    if (fragmentIndex !== -1) {
      cleanUrl = cleanUrl.substring(0, fragmentIndex);
    }

    return cleanUrl;
  }

  /**
   * Check if user has ALL required permissions
   */
  private checkAllPermissions(requiredPermissions: PermissionBit[], userPermissions: bigint): boolean {
    if (requiredPermissions.length === 0) {
      return true;
    }

    return requiredPermissions.every((permission) => (userPermissions & permission) === permission);
  }
}
